'use client';

import { Radio } from '@base-ui/react/radio';
import { RadioGroup as RadioGroupPrimitive } from '@base-ui/react/radio-group';
import { cn } from '@zealot/ui/lib/utils';
import type * as React from 'react';

function RadioGroup({ className, ...props }: RadioGroupPrimitive.Props) {
  return <RadioGroupPrimitive data-slot="radio-group" className={cn('grid gap-2', className)} {...props} />;
}

function RadioGroupItem({ className, ...props }: Radio.Root.Props) {
  return (
    <Radio.Root
      data-slot="radio-group-item"
      className={cn(
        'flex aspect-square h-4 w-4 shrink-0 items-center justify-center rounded-full border border-primary text-primary shadow-sm',
        'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
        'data-disabled:cursor-not-allowed data-disabled:opacity-50',
        className,
      )}
      {...props}
    >
      <Radio.Indicator className="flex items-center justify-center">
        <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor" className="h-2 w-2 fill-primary">
          <circle cx="5" cy="5" r="5" />
        </svg>
      </Radio.Indicator>
    </Radio.Root>
  );
}

type RadioGroupCardProps = Radio.Root.Props & {
  children: React.ReactNode;
};

function RadioGroupCard({ className, children, ...props }: RadioGroupCardProps) {
  return (
    <Radio.Root
      data-slot="radio-group-card"
      render={<label />}
      className={(state) =>
        cn(
          'flex cursor-pointer items-start gap-3 rounded-lg border p-4 text-left transition-colors',
          'focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
          state.checked ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/30',
          state.disabled && 'cursor-not-allowed opacity-50',
          typeof className === 'function' ? className(state) : className,
        )
      }
      {...props}
    >
      <div className="flex items-center justify-center pt-0.5">
        <div className="flex aspect-square h-4 w-4 shrink-0 items-center justify-center rounded-full border border-primary shadow-sm">
          <Radio.Indicator className="flex h-full w-full items-center justify-center">
            <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor" className="h-2 w-2 fill-primary">
              <circle cx="5" cy="5" r="5" />
            </svg>
          </Radio.Indicator>
        </div>
      </div>
      <div className="flex-1">{children}</div>
    </Radio.Root>
  );
}

export { RadioGroup, RadioGroupCard, RadioGroupItem };
